// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * Web Contact Picker provider.
 * Gebruikt de officiële browser-API `navigator.contacts.select` (Contact Picker API).
 * Beschikbaar op Android/Chromium. De browser toont de echte OS-contactkiezer; WilsApp
 * krijgt uitsluitend de door de gebruiker geselecteerde contacten terug - nooit het hele
 * adresboek. Dat is privacy by design en kan niet worden omzeild vanuit een webapp.
 */
import type { ContactProvider, ProvidedContact } from '../types'

// Minimale typing voor de (nog niet in lib.dom standaard) Contact Picker API.
interface ContactsManagerLike {
  select(props: string[], opts?: { multiple?: boolean }): Promise<Array<{ name?: string[]; email?: string[]; tel?: string[] }>>
  getProperties?(): Promise<string[]>
}

function manager(): ContactsManagerLike | null {
  const nav = navigator as unknown as { contacts?: ContactsManagerLike }
  return nav.contacts && typeof nav.contacts.select === 'function' ? nav.contacts : null
}

export const webPickerProvider: ContactProvider = {
  id: 'web-picker',
  label: 'Telefooncontacten',
  description: 'Kies uit de contacten op uw telefoon.',
  capabilities: { multiSelect: true, exposesFullList: false, inAppSearch: false },

  isSupported() {
    return typeof navigator !== 'undefined' && manager() !== null
  },

  async pick(): Promise<ProvidedContact[]> {
    const mgr = manager()
    if (!mgr) return []

    const wanted = ['name', 'email', 'tel']
    const available = mgr.getProperties ? await mgr.getProperties() : wanted
    const props = wanted.filter(p => available.includes(p))

    const picked = await mgr.select(props, { multiple: true })
    return (picked ?? []).map((c, i): ProvidedContact => {
      const phone = (c.tel?.[0] ?? '').trim()
      const email = (c.email?.[0] ?? '').trim()
      const name  = (c.name?.[0] ?? '').trim()
      return {
        id: `web-${i}-${phone || email || name}`,
        name: name || 'Onbekend contact',
        phone: phone || undefined,
        email: email || undefined,
        source: 'web-picker',
      }
    })
  },
}
